import { useState, useCallback } from "react";

/**
 * ドラッグ&ドロップ並べ替え用カスタムフック
 * @param {Array} items - 並べ替え対象の配列
 * @param {Function} onReorder - 並べ替え完了時のコールバック関数
 * @param {Object} options - オプション設定
 * @returns {Object} ドラッグ&ドロップ関連の状態とハンドラー
 */
export const useDragAndDrop = (items, onReorder, options = {}) => {
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);

  const {
    borderColor = "#fff",
    backgroundColor = "#ffffff40",
    draggedBackgroundColor = "#ffffff20",
  } = options;

  // ドラッグ開始ハンドラー
  const handleDragStart = useCallback((e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");

    // ドラッグ中の視覚的フィードバック
    e.target.style.opacity = "0.5";
    e.target.style.cursor = "grabbing";
  }, []);

  // ドラッグ終了ハンドラー
  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = "1";
    e.target.style.cursor = "grab";
    setDraggedItemIndex(null);
    setDragOverPosition(null);
  }, []);

  // ドラッグオーバーハンドラー
  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      // マウス位置を取得して上半分か下半分かを判定
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const middleY = rect.top + rect.height / 2;
      const position = mouseY < middleY ? "top" : "bottom";

      setDragOverPosition(position);

      // ドロップ可能な視覚的フィードバック
      e.currentTarget.style.backgroundColor = backgroundColor;

      // 境界線を表示
      if (position === "top") {
        e.currentTarget.style.borderTop = `3px solid ${borderColor}`;
        e.currentTarget.style.borderBottom = "";
      } else {
        e.currentTarget.style.borderBottom = `3px solid ${borderColor}`;
        e.currentTarget.style.borderTop = "";
      }
    },
    [borderColor, backgroundColor]
  );

  // ドラッグリーブハンドラー
  const handleDragLeave = useCallback(
    (e) => {
      const index = parseInt(e.currentTarget.dataset.index);
      e.currentTarget.style.backgroundColor =
        draggedItemIndex === index ? draggedBackgroundColor : "transparent";
      e.currentTarget.style.borderTop = "";
      e.currentTarget.style.borderBottom = "";
      setDragOverPosition(null);
    },
    [draggedItemIndex, draggedBackgroundColor]
  );

  // ドロップハンドラー
  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();

      // スタイルをリセット
      e.currentTarget.style.backgroundColor = "transparent";
      e.currentTarget.style.borderTop = "";
      e.currentTarget.style.borderBottom = "";

      if (draggedItemIndex === null) {
        setDragOverPosition(null);
        return;
      }

      // マウス位置を再取得して挿入位置を決定
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const middleY = rect.top + rect.height / 2;
      const isTopHalf = mouseY < middleY;

      let targetIndex;

      if (isTopHalf) {
        // 上半分にドロップ：そのインデックスの前に挿入
        targetIndex = dropIndex;
      } else {
        // 下半分にドロップ：そのインデックスの後に挿入
        targetIndex = dropIndex + 1;
      }

      // ドラッグ元のインデックスを考慮
      if (draggedItemIndex < targetIndex) {
        targetIndex = targetIndex - 1;
      }

      // 同じ位置への移動は何もしない
      if (draggedItemIndex === targetIndex) {
        setDragOverPosition(null);
        return;
      }

      // 配列の並び替えを実行
      const newItems = [...items];
      const draggedItem = newItems[draggedItemIndex];

      // ドラッグされたアイテムを削除
      newItems.splice(draggedItemIndex, 1);

      // 新しい位置に挿入
      newItems.splice(targetIndex, 0, draggedItem);

      // 並び替え完了コールバック
      onReorder(newItems);

      setDragOverPosition(null);
    },
    [draggedItemIndex, items, onReorder]
  );

  // ドラッグ可能な要素に適用するプロパティ
  const getDraggableProps = useCallback(
    (index) => ({
      draggable: true,
      onDragStart: (e) => handleDragStart(e, index),
      onDragEnd: handleDragEnd,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: (e) => handleDrop(e, index),
      "data-index": index,
      style: {
        cursor: "grab",
        transition: "all 0.2s ease",
        backgroundColor:
          draggedItemIndex === index ? draggedBackgroundColor : "transparent",
        position: "relative",
      },
    }),
    [
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      draggedItemIndex,
      draggedBackgroundColor,
    ]
  );

  return {
    draggedItemIndex,
    dragOverPosition,
    getDraggableProps,
  };
};
